import useExtendedFormContext from '@components/form-hook-wrapper/hooks/useExtendedFormContext';
import { TextDisplay } from '@components/text-display/TextDisplay';
import styled from '@emotion/styled';
import onChangeDropdown from '@helpers/@utils/dropdown-onchange';
import isFullEmptyObject from '@helpers/@utils/is-full-empty-object';
import { Dropdown } from '@mis/sushi-tailwind-react';
import { FieldController } from '@model/field-controller';
import React from 'react';
import { Controller, FieldPath, FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

const CustomDropdown = styled(Dropdown)`
  & .ss-select-selection-item {
    color: black !important;
  }
`;

export type InputDropdownProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
> = FieldController<TFieldValues, TName> &
  React.ComponentProps<typeof Dropdown> & {
    viewOnly?: boolean;
    overModal?: boolean;
  };

const InputDropdown = <TFieldValues extends FieldValues = FieldValues, TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>>({
  name,
  rules,
  placeholder,
  options = [],
  onChange,
  viewOnly,
  overModal,
  ...props
}: InputDropdownProps<TFieldValues, TName>): React.ReactElement => {
  const { t } = useTranslation('error');
  const { control, displayErrorMessage } = useExtendedFormContext<TFieldValues>();

  const onDropdownChange = (onchange: (...event: AnyValue[]) => void) => (val: AnyValue, options: AnyValue) => {
    onChangeDropdown(name, onchange)(val || null);
    onChange && onChange(val || null, options);
  };

  const dropdownStyle = React.useMemo<React.CSSProperties | undefined>(() => {
    let style: React.CSSProperties = {};
    if (props.dropdownRender) {
      style = { ...style, ...props.dropdownStyle };
    }
    if (overModal) {
      style = { ...style, zIndex: 10000 };
    }
    return !isFullEmptyObject(style) ? style : undefined;
  }, [overModal, props.dropdownRender, props.dropdownStyle]);

  return (
    <Controller
      name={name}
      rules={rules}
      control={control}
      render={({ field, fieldState: { error } }) => {
        if (viewOnly) {
          return (
            <TextDisplay
              type={name}
              value={options.find((i) => i.value === field.value)?.label || field.value}
              placeholder={typeof placeholder === 'string' ? placeholder : undefined}
            />
          );
        }
        return (
          <CustomDropdown
            showSearch
            optionFilterProp="label"
            {...field}
            {...props}
            onChange={onDropdownChange(field.onChange)}
            options={options}
            placeholder={placeholder}
            status={!props.disabled && displayErrorMessage && error ? 'error' : undefined}
            message={!props.disabled && displayErrorMessage && error ? t(error?.message as string) : undefined}
            data-testid={field.name}
            dropdownStyle={dropdownStyle}
            showArrow={props.showArrow || !props.disabled}
            notFoundContent={props.notFoundContent || <div className="cursor-default bg-white p-2 px-3">{t('translation:no_data')}</div>}
          />
        );
      }}
    />
  );
};

export default InputDropdown;
